import { Button } from 'primereact/button';
import { Column } from 'primereact/column';
import { confirmDialog } from 'primereact/confirmdialog';
import { DataTable } from 'primereact/datatable';
import { Dialog } from 'primereact/dialog';
import { Dropdown } from 'primereact/dropdown';
import { InputText } from 'primereact/inputtext';
import { InputTextarea } from 'primereact/inputtextarea';
import { Tag } from 'primereact/tag';
import { useEffect, useState } from 'react';
import { makeRequest2 } from '../../../../../utils/makeRequest';
import { toastify } from '../../../../../utils/toast';
import {
    getKnowledgeBaseArticlesURL,
    createKnowledgeBaseArticleURL,
    updateKnowledgeBaseArticleURL,
    deleteKnowledgeBaseArticleURL,
    getTicketCategoriesURL
} from '../../../../../utils/urls';

const KnowledgeBaseTab = () => {
    const [articles, setArticles] = useState([]);
    const [filteredArticles, setFilteredArticles] = useState([]);
    const [globalFilter, setGlobalFilter] = useState('');
    const [loading, setLoading] = useState(false);
    const [dialogVisible, setDialogVisible] = useState(false);
    const [isEdit, setIsEdit] = useState(false);
    const [selectedArticle, setSelectedArticle] = useState(null);
    const [categories, setCategories] = useState([]);

    const [formData, setFormData] = useState({
        title: '',
        content: '',
        category_id: '',
        tags: '',
        status: 'draft',
        visibility: 'internal'
    });

    const categoryOptions = categories.map(cat => ({
        label: cat.name,
        value: cat._id
    }));

    const statusOptions = [
        { label: 'Draft', value: 'draft' },
        { label: 'Published', value: 'published' }
    ];

    const visibilityOptions = [
        { label: 'Internal', value: 'internal' },
        { label: 'Public', value: 'public' }
    ];

    useEffect(() => {
        loadCategories();
        loadArticles();
    }, []);

    const loadCategories = async () => {
        try {
            const response = await makeRequest2(getTicketCategoriesURL, 'GET', {});
            if (response.success && response.data && response.data.success) {
                const categoriesData = response.data.data || [];
                setCategories(categoriesData);
            }
        } catch (error) {
            console.error('Error loading categories:', error);
        }
    };

    const loadArticles = async () => {
        setLoading(true);
        try {
            const response = await makeRequest2(getKnowledgeBaseArticlesURL, 'GET', {});

            if (response.success && response.data && response.data.success) {
                const articlesData = response.data.data.articles || [];
                setArticles(articlesData);
                setFilteredArticles(articlesData);
            } else if (response.success === false || (response.data && response.data.success === false)) {
                const errorData = response.data || response;
                toastify(errorData.error || errorData.message || 'Failed to load articles', 'error');
                setArticles([]);
                setFilteredArticles([]);
            } else {
                toastify('Failed to load articles', 'error');
                setArticles([]);
                setFilteredArticles([]);
            }
        } catch (error) {
            toastify(error.message || 'Error loading articles', 'error');
            setArticles([]);
            setFilteredArticles([]);
        }
        setLoading(false);
    };

    const onGlobalFilterChange = (e) => {
        const value = e.target.value.toLowerCase();
        setGlobalFilter(value);

        if (!value.trim()) {
            setFilteredArticles(articles);
        } else {
            const filtered = articles.filter(article => {
                return (
                    article.title?.toLowerCase().includes(value) ||
                    article.content?.toLowerCase().includes(value) ||
                    article.category?.toLowerCase().includes(value) ||
                    article.tags?.some(tag => tag.toLowerCase().includes(value))
                );
            });
            setFilteredArticles(filtered);
        }
    };

    const handleAddArticle = () => {
        setIsEdit(false);
        setFormData({
            title: '',
            content: '',
            category_id: '',
            tags: '',
            status: 'draft',
            visibility: 'internal'
        });
        setSelectedArticle(null);
        setDialogVisible(true);
    };

    const handleEditArticle = (article) => {
        setIsEdit(true);
        setSelectedArticle(article);
        setFormData({
            title: article.title || '',
            content: article.content || '',
            category_id: article.category_id?._id || article.category_id || '',
            tags: Array.isArray(article.tags) ? article.tags.join(', ') : '',
            status: article.status || 'draft',
            visibility: article.visibility || 'internal'
        });
        setDialogVisible(true);
    };

    const handleSaveArticle = async () => {
        if (!formData.title.trim()) {
            toastify('Title is required', 'error');
            return;
        }

        if (!formData.content.trim()) {
            toastify('Content is required', 'error');
            return;
        }

        if (!formData.category_id) {
            toastify('Category is required', 'error');
            return;
        }

        try {
            const payload = {
                ...formData,
                tags: formData.tags.split(',').map(tag => tag.trim()).filter(tag => tag)
            };

            let response;
            if (isEdit) {
                const url = updateKnowledgeBaseArticleURL.replace(':article_id', selectedArticle._id || selectedArticle.id);
                response = await makeRequest2(url, 'PUT', payload);
            } else {
                response = await makeRequest2(createKnowledgeBaseArticleURL, 'POST', payload);
            }

            if (response.success && response.data && response.data.success) {
                toastify(response.data.message || (isEdit ? 'Article updated successfully' : 'Article created successfully'), 'success');
                setDialogVisible(false);
                loadArticles();
            } else {
                const errorMsg = response.data?.error || response.error || 'Failed to save article';
                toastify(errorMsg, 'error');
            }
        } catch (error) {
            toastify(error.message || 'Failed to save article', 'error');
        }
    };

    const handleArchiveArticle = (article) => {
        confirmDialog({
            message: `Are you sure you want to archive "${article.title}"? This article will be hidden but can be restored later.`,
            header: 'Confirm Archive',
            icon: 'pi pi-exclamation-triangle',
            acceptClassName: 'p-button-warning',
            accept: () => performArchive(article)
        });
    };

    const performArchive = async (article) => {
        try {
            const url = deleteKnowledgeBaseArticleURL.replace(':article_id', article._id || article.id);
            const response = await makeRequest2(url, 'DELETE', {});

            if (response.success && response.data && response.data.success) {
                toastify(response.data.message || 'Article archived successfully', 'success');
                loadArticles();
            } else {
                const errorMsg = response.data?.error || response.error || 'Failed to archive article';

                // Show error dialog
                confirmDialog({
                    message: errorMsg,
                    header: 'Cannot Archive Article',
                    icon: 'pi pi-info-circle',
                    acceptLabel: 'OK',
                    rejectClassName: 'hidden',
                    accept: () => {}
                });
            }
        } catch (error) {
            const errorMsg = error.message || 'Failed to archive article';
            confirmDialog({
                message: errorMsg,
                header: 'Error',
                icon: 'pi pi-times-circle',
                acceptLabel: 'OK',
                rejectClassName: 'hidden',
                accept: () => {}
            });
        }
    };

    const statusBodyTemplate = (rowData) => {
        const severity = rowData.status === 'published' ? 'success' : 'warning';
        return <Tag value={rowData.status} severity={severity} />;
    };

    const visibilityBodyTemplate = (rowData) => {
        const severity = rowData.visibility === 'public' ? 'info' : 'secondary';
        return <Tag value={rowData.visibility} severity={severity} />;
    };

    const tagsBodyTemplate = (rowData) => {
        if (!rowData.tags || rowData.tags.length === 0) return 'N/A';
        return rowData.tags.join(', ');
    };

    const actionBodyTemplate = (rowData) => {
        return (
            <div style={{ display: 'flex', gap: '4px', alignItems: 'center' }}>
                <Button
                    icon="pi pi-pencil"
                    className="p-button-rounded p-button-success p-button-sm"
                    onClick={() => handleEditArticle(rowData)}
                    tooltip="Edit Article"
                />
                <Button
                    icon="pi pi-box"
                    className="p-button-rounded p-button-warning p-button-sm"
                    onClick={() => handleArchiveArticle(rowData)}
                    tooltip="Archive Article"
                />
            </div>
        );
    };

    const dialogFooter = (
        <div>
            <Button label="Cancel" icon="pi pi-times" onClick={() => setDialogVisible(false)} className="p-button-text" />
            <Button label="Save" icon="pi pi-check" onClick={handleSaveArticle} />
        </div>
    );

    return (
        <>
            <div className="row">
                <div className="col-sm-12">
                    <div className="card">
                        <div className="card-header">
                            <div className="row align-items-center">
                                <div className="col-md-4">
                                    <h5 className="mb-0">Knowledge Base Articles</h5>
                                </div>
                                <div className="col-md-8">
                                    <div className="d-flex gap-2 justify-content-end">
                                        <div className="p-input-icon-left" style={{ minWidth: '300px' }}>
                                            <i className="pi pi-search" />
                                            <InputText
                                                value={globalFilter}
                                                onChange={onGlobalFilterChange}
                                                placeholder="Search articles..."
                                                className="w-100"
                                            />
                                        </div>
                                        <button
                                            className="btn btn-primary"
                                            style={{ color: 'white', whiteSpace: 'nowrap' }}
                                            onClick={handleAddArticle}
                                        >
                                            <i className="ti ti-plus" />
                                            &nbsp;Add New Article
                                        </button>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div className="card-body">
                            <div className="dt-responsive table-responsive">
                                <DataTable
                                    value={filteredArticles}
                                    emptyMessage="No articles found."
                                    sortMode="multiple"
                                    paginator
                                    rows={10}
                                    stripedRows
                                    tableStyle={{ minWidth: '50rem' }}
                                    loading={loading}
                                >
                                    <Column
                                        field="title"
                                        header="Title"
                                        sortable
                                        style={{ width: '25%' }}
                                    />
                                    <Column
                                        field="category"
                                        header="Category"
                                        sortable
                                        style={{ width: '12%' }}
                                    />
                                    <Column
                                        header="Tags"
                                        body={tagsBodyTemplate}
                                        style={{ width: '18%' }}
                                    />
                                    <Column
                                        header="Status"
                                        body={statusBodyTemplate}
                                        sortable
                                        style={{ width: '10%' }}
                                    />
                                    <Column
                                        header="Visibility"
                                        body={visibilityBodyTemplate}
                                        sortable
                                        style={{ width: '10%' }}
                                    />
                                    <Column
                                        field="created_at"
                                        header="Created"
                                        sortable
                                        style={{ width: '12%' }}
                                        body={(rowData) => new Date(rowData.created_at).toLocaleDateString()}
                                    />
                                    <Column
                                        header="Actions"
                                        body={actionBodyTemplate}
                                        exportable={false}
                                        style={{ width: '13%' }}
                                    />
                                </DataTable>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <Dialog
                visible={dialogVisible}
                style={{ width: '50vw' }}
                header={isEdit ? 'Edit Article' : 'Add New Article'}
                modal
                footer={dialogFooter}
                onHide={() => setDialogVisible(false)}
            >
                <div className="p-fluid">
                    <div className="field mb-3">
                        <label htmlFor="title">Title *</label>
                        <InputText
                            id="title"
                            value={formData.title}
                            onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                        />
                    </div>

                    <div className="field mb-3">
                        <label htmlFor="category">Category *</label>
                        <Dropdown
                            id="category"
                            value={formData.category_id}
                            options={categoryOptions}
                            onChange={(e) => setFormData({ ...formData, category_id: e.value })}
                            placeholder="Select a category"
                        />
                    </div>

                    <div className="field mb-3">
                        <label htmlFor="content">Content *</label>
                        <InputTextarea
                            id="content"
                            value={formData.content}
                            onChange={(e) => setFormData({ ...formData, content: e.target.value })}
                            rows={8}
                        />
                    </div>

                    <div className="field mb-3">
                        <label htmlFor="tags">Tags (comma-separated)</label>
                        <InputText
                            id="tags"
                            value={formData.tags}
                            onChange={(e) => setFormData({ ...formData, tags: e.target.value })}
                            placeholder="e.g. payment, refund, billing"
                        />
                    </div>

                    <div className="field mb-3">
                        <label htmlFor="status">Status *</label>
                        <Dropdown
                            id="status"
                            value={formData.status}
                            options={statusOptions}
                            onChange={(e) => setFormData({ ...formData, status: e.value })}
                        />
                    </div>

                    <div className="field mb-3">
                        <label htmlFor="visibility">Visibility *</label>
                        <Dropdown
                            id="visibility"
                            value={formData.visibility}
                            options={visibilityOptions}
                            onChange={(e) => setFormData({ ...formData, visibility: e.value })}
                        />
                    </div>
                </div>
            </Dialog>
        </>
    );
};

export default KnowledgeBaseTab;
